import { useCallback, useEffect, useRef, useState } from 'react';
import { guidedOpensOnMount, type GuidedOpen } from '../../shared/guided_open.js';
import { useFocusAfterCommit } from '../../shared/guided_flow.js';
import type { HotelSearchValue } from '../models.js';
import { pendingHotelSearchFields, type HotelSearchPendingField } from './hotel_search_pending.js';

/*
 * A MÁQUINA DE PAINÉIS da barra de busca de hotel: um painel aberto por vez
 * (`activePanel`), os gatilhos a que o foco volta, o fechamento por clique fora
 * e Escape na casca larga, e a continuação do preenchimento guiado
 * (`afterPick`). Saiu de `hotel_search_bar.tsx` pelo teto de linhas dele; a
 * barra e as colunas dela são os únicos consumidores.
 */

export type Panel = 'dates' | 'rooms' | 'places' | null;

/** O preenchimento guiado da barra: se está LIGADO (a prop, com a barra
 *  destravada — nos dois palcos, página e cápsula do card), qual campo aponta
 *  agora (`wanted`, o anel — o primeiro pendente da fileira; `null` = nada
 *  pendente ou sem guia) e qual painel a barra abre ao NASCER (`mount`): o
 *  primeiro pendente, no palco que o host pede (`guidedOpen`: a página só na
 *  fileira larga, o card da conversa também na folha estreita, o card
 *  remontado do histórico nunca). */
export function guidedField(
  props: {
    readonly guided?: boolean;
    readonly guidedOpen?: GuidedOpen;
    readonly value: HotelSearchValue;
  },
  disabled: boolean,
  narrow: boolean,
): { readonly on: boolean; readonly wanted: Panel; readonly mount: Panel } {
  const on = props.guided === true && !disabled;
  const wanted = on ? (pendingHotelSearchFields(props.value)[0] ?? null) : null;
  return { on, wanted, mount: guidedOpensOnMount(props.guidedOpen, narrow) ? wanted : null };
}

/** O rascunho de digitação SOMBREIA o destino confirmado, então ele morre com
   o popover: fechado, não há o que escolher e o campo tem de voltar a
   mostrar o que a busca realmente vai usar. Sem isto, sair do popover sem
   escolher (Escape, clique fora) deixava na tela um texto que o critério
   nunca teve — e um destino trocado POR FORA do campo (o pré-preenchimento
   dos atalhos da página de busca) ficava invisível atrás dele.
   Escolher pelo popover segue zerando o rascunho na hora (`onPick`): lá o
   reset não pode esperar o fechamento animar. */
export function usePlaceDraft(activePanel: Panel) {
  const [placeQuery, setPlaceQuery] = useState<string | null>(null);
  // On the panel's change, during render.
  const [seenPanel, setSeenPanel] = useState(activePanel);
  if (seenPanel !== activePanel) {
    setSeenPanel(activePanel);
    if (activePanel !== 'places') setPlaceQuery(null);
  }
  return [placeQuery, setPlaceQuery] as const;
}

export function useHotelSearchPanels(
  disabled: boolean,
  sheet: boolean,
  guide: { readonly on: boolean; readonly mount: Panel },
) {
  const guided = guide.on;
  // `mount` é lido UMA vez — é o estado inicial, não uma abertura por effect.
  const [requestedPanel, setRequestedPanel] = useState<Panel>(guide.mount);
  const rootRef = useRef<HTMLDivElement>(null);
  const datesTriggerRef = useRef<HTMLButtonElement>(null);
  const roomsTriggerRef = useRef<HTMLButtonElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  /** O foco no Buscar depois de uma resposta: espera o commit, que destrava. */
  const focusSearch = useFocusAfterCommit(searchTriggerRef);
  const activePanel = disabled ? null : requestedPanel;

  // Travada, a barra não guarda painel para reabrir quando destravar.
  if (disabled && requestedPanel !== null) setRequestedPanel(null);

  const triggerFor = useCallback((panel: Exclude<Panel, null>): HTMLElement | null => {
    if (panel === 'places') {
      // O `Onde` é o `PlaceField` (pílula-gatilho, sem input fixo): o alvo do
      // foco de volta é a própria pílula.
      return rootRef.current?.querySelector<HTMLElement>('[data-place-field-trigger]') ?? null;
    }
    if (panel === 'dates') return datesTriggerRef.current;
    return roomsTriggerRef.current;
  }, []);
  const dismissPanel = useCallback(
    (panel: Exclude<Panel, null>, restoreFocus: boolean) => {
      if (restoreFocus) triggerFor(panel)?.focus();
      setRequestedPanel((current) => (current === panel ? null : current));
    },
    [triggerFor],
  );

  useEffect(() => {
    // Na FOLHA (da conversa ou de um campo da página estreita) os painéis não
    // flutuam: quem fecha por clique fora e Escape é o drawer, e um toque
    // dentro dela seria "fora" da pílula pra este listener.
    if (!activePanel || sheet) return undefined;
    const panel = activePanel;
    const onPointerDown = (event: PointerEvent) => {
      // O `Onde` (`PlaceField`) já fecha no clique fora pelo próprio popover
      // do Base UI — e é ele quem sabe que o fechamento veio de um ponteiro e
      // deixa o foco onde a pessoa clicou. Fechar daqui antes (este listener é
      // de captura) viraria um fechamento programático, e o popover devolveria
      // o foco ao gatilho, roubando o clique.
      // O `Quem` (`HotelRoomsPickerField`) é o mesmo Popover do DS, pelo mesmo motivo.
      if (panel === 'places' || panel === 'rooms') return;
      if (!(event.target instanceof Node) || rootRef.current?.contains(event.target)) return;
      // Camadas que saem por PORTAL continuam sendo "dentro": o popup do
      // `PlaceField` (o `Onde` da página) mora no body, e sem este guard
      // escolher um lugar contaria como clique fora — o painel fechava antes
      // de o clique do item resolver.
      if (
        event.target instanceof Element &&
        event.target.closest('[data-slot="popover-content"]')
      ) {
        return;
      }
      dismissPanel(panel, false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // O Escape do `Quem` é do Popover do DS, como o clique fora: ele sabe que
      // o menu ⋯ de uma vaga é uma camada acima e fecha só o menu, e devolve
      // o foco ao gatilho quando fecha o painel.
      if (panel === 'rooms') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      dismissPanel(panel, true);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [activePanel, dismissPanel, sheet]);

  /** O que vem DEPOIS de responder um campo. Sem guia, o `fallback` de sempre
   *  (fechar o painel). Com o preenchimento guiado, a continuação do gesto:
   *  abre o painel do próximo campo pendente do valor RECÉM-gravado ou, com
   *  tudo respondido, fecha e pousa o foco no Buscar. Lê o valor novo (e não
   *  `props.value`, que ainda é o de antes do commit) porque quem chama acabou
   *  de mudá-lo. */
  const afterPick = useCallback(
    (next: HotelSearchValue, fallback: () => void) => {
      if (!guided) {
        fallback();
        return;
      }
      const pending: HotelSearchPendingField | undefined = pendingHotelSearchFields(next)[0];
      setRequestedPanel(pending ?? null);
      if (!pending) focusSearch();
    },
    [guided, focusSearch],
  );

  return {
    activePanel,
    setRequestedPanel,
    dismissPanel,
    guided,
    /** O painel que a barra abriu ao nascer (ver `guidedField`). */
    mount: guide.mount,
    afterPick,
    focusSearch,
    rootRef,
    datesTriggerRef,
    roomsTriggerRef,
    searchTriggerRef,
  };
}

export type HotelSearchPanels = ReturnType<typeof useHotelSearchPanels>;
